import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BusinessFormDialog, { EMPTY_BUSINESS } from "@/components/BusinessFormDialog";
import { toast } from "sonner";
import EmailSettings from "@/components/EmailSettings";
import SocialSettings from "@/components/SocialSettings";
import EmailCenter from "@/components/EmailCenter";
import MissingImagesButton from "@/components/MissingImagesButton";
import ArticlesPanel from "@/components/ArticlesPanel";
import AdminAlertsPanel from "@/components/AdminAlertsPanel";
import SoldDealsPanel from "@/components/SoldDealsPanel";
import SoldDealDialog, { dealFromBusiness } from "@/components/SoldDealDialog";
import InquiriesPanel from "@/components/InquiriesPanel";
import ContactMessagesPanel from "@/components/ContactMessagesPanel";
import DiscoveryRequestsPanel from "@/components/DiscoveryRequestsPanel";
import InvitationsPanel from "@/components/InvitationsPanel";
import BusinessReviewDialog from "@/components/BusinessReviewDialog";
import { VisitReportsPanel, MediaRequestsPanel } from "@/components/AdminExtraPanels";
import { Plus, Pencil, Trash2, Building2, Inbox, CheckCircle2, Clock, ShieldCheck, TrendingUp, Users, Euro, Settings, MessageSquare, CalendarCheck, UserPlus, Eye, ClipboardList, Camera, Mail, Handshake, Newspaper, BellRing } from "lucide-react";

const STATUS = {
  available: { label: "Disponible", cls: "bg-green-100 text-green-700" },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  sold: { label: "Cédée", cls: "bg-red-100 text-red-700" },
};

export default function Admin() {
  const [items, setItems] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initial, setInitial] = useState(EMPTY_BUSINESS);
  const [dealDraft, setDealDraft] = useState(null);
  const [dealsVersion, setDealsVersion] = useState(0);
  const [linkedIds, setLinkedIds] = useState([]);

  const load = () => {
    api.get("/admin/businesses").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/admin/nda-signatures").then((r) => setSignatures(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/sold-deals").then((r) => setLinkedIds(r.data.map((d) => d.business_id).filter(Boolean))).catch(() => {});
  };
  const dealSaved = () => { setDealsVersion((v) => v + 1); load(); };
  const offerDeal = (b) => toast.success("Entreprise marquée « Cédée »", { description: "L'ajouter à la page « Nos dernières cessions » ?", duration: 10000, action: { label: "Ajouter", onClick: () => setDealDraft(dealFromBusiness(b)) } });
  useEffect(load, []);

  const openNew = () => { setEditing(null); setInitial(EMPTY_BUSINESS); setOpen(true); };
  const openEdit = (b) => { setEditing(b.business_id); setInitial({ ...EMPTY_BUSINESS, ...b }); setOpen(true); };

  const save = async (payload) => {
    try {
      const prev = items.find((b) => b.business_id === editing);
      const { data } = editing ? await api.put(`/admin/businesses/${editing}`, payload) : await api.post("/admin/businesses", payload);
      const saved = { ...prev, ...payload, ...(data || {}) };
      if (payload.status === "sold" && prev?.status !== "sold" && !linkedIds.includes(saved.business_id)) offerDeal(saved);
      else toast.success(editing ? "Entreprise mise à jour" : "Entreprise ajoutée");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entreprise ?")) return;
    try { await api.delete(`/admin/businesses/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approve = async (id) => {
    try { await api.patch(`/admin/businesses/${id}/status`, { status: "available" }); toast.success("Annonce publiée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pendingCount = items.filter((b) => b.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#FDFDFC]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs">Back-office administrateur</span>
            <h1 className="mt-2 font-display text-3xl text-[#1B2A4A]">Gestion du catalogue</h1>
            {pendingCount > 0 && <p className="mt-2 text-sm text-amber-700 flex items-center gap-1.5"><Clock className="w-4 h-4" /> {pendingCount} annonce(s) en attente de validation</p>}
          </div>
          <div className="flex flex-wrap gap-3">
            <MissingImagesButton onDone={load} />
            <Button data-testid="admin-onsite-btn" onClick={() => navigate("/admin/rdv")} className="rounded-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white px-6"><CalendarCheck className="w-4 h-4 mr-1.5 text-[#E0C173]" /> Mode RDV : saisie sur place</Button>
            <Button data-testid="admin-add-btn" onClick={openNew} className="rounded-none bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold"><Plus className="w-4 h-4 mr-1" /> Ajouter une entreprise</Button>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-px bg-gray-200 border border-gray-200" data-testid="admin-kpis">
            {[
              { label: "Annonces", value: stats.total, icon: Building2 },
              { label: "En attente", value: stats.pending, icon: Clock, accent: stats.pending > 0 },
              { label: "En ligne", value: stats.available, icon: CheckCircle2 },
              { label: "Demandes à traiter", id: "inquiries-new", value: stats.inquiries_new, icon: Inbox, accent: stats.inquiries_new > 0 },
              { label: "RDV à rappeler", id: "rdv-new", value: stats.discovery_new, icon: CalendarCheck, accent: stats.discovery_new > 0 },
              { label: "Demandes média", id: "media-new", value: stats.media_new, icon: Camera, accent: stats.media_new > 0 },
              { label: "NDA signés", value: stats.nda_signed, icon: ShieldCheck },
            ].map((k) => (
              <div key={k.label} className={`bg-white p-5 ${k.accent ? "ring-1 ring-inset ring-amber-300" : ""}`} data-testid={`kpi-${k.id || k.label}`}>
                <k.icon className={`w-5 h-5 mb-2 ${k.accent ? "text-amber-600" : "text-[#E0C173]"}`} strokeWidth={1.5} />
                <div className={`font-display text-2xl ${k.accent ? "text-amber-700" : "text-[#1B2A4A]"}`}>{k.value}</div>
                <div className="text-xs uppercase tracking-widest text-[#475569] mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="businesses" className="mt-8">
          <TabsList className="rounded-none bg-white border border-gray-200 h-auto flex-wrap justify-start">
            <TabsTrigger value="businesses" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Building2 className="w-4 h-4 mr-1" /> Entreprises ({items.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Inbox className="w-4 h-4 mr-1" /> Demandes ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="nda" data-testid="admin-tab-nda" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><ShieldCheck className="w-4 h-4 mr-1" /> Signataires NDA ({signatures.length})</TabsTrigger>
            <TabsTrigger value="rdv" data-testid="admin-tab-rdv" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><CalendarCheck className="w-4 h-4 mr-1" /> RDV découverte</TabsTrigger>
            <TabsTrigger value="visits" data-testid="admin-tab-visits" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><ClipboardList className="w-4 h-4 mr-1" /> Fiches de visite</TabsTrigger>
            <TabsTrigger value="media" data-testid="admin-tab-media" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Camera className="w-4 h-4 mr-1" /> Demandes média</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="admin-tab-invitations" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><UserPlus className="w-4 h-4 mr-1" /> Invitations cédants</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><MessageSquare className="w-4 h-4 mr-1" /> Messages contact</TabsTrigger>
            <TabsTrigger value="deals" data-testid="admin-tab-deals" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Handshake className="w-4 h-4 mr-1" /> Cessions réalisées</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="admin-tab-alerts" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><BellRing className="w-4 h-4 mr-1" /> Alertes</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Newspaper className="w-4 h-4 mr-1" /> Blog</TabsTrigger>
            <TabsTrigger value="emails" data-testid="admin-tab-emails" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Mail className="w-4 h-4 mr-1" /> Emails</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-none data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"><Settings className="w-4 h-4 mr-1" /> Paramètres</TabsTrigger>
          </TabsList>

          <TabsContent value="businesses" className="mt-6">
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-[#475569]">
                  <tr><th className="p-4">Entreprise</th><th className="p-4">Secteur</th><th className="p-4">Région</th><th className="p-4">Rentab.</th><th className="p-4">Statut</th><th className="p-4 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((b) => {
                    const st = STATUS[b.status] || STATUS.available;
                    return (
                      <tr key={b.business_id} data-testid={`admin-row-${b.business_id}`} className="border-t border-gray-100">
                        <td className="p-4 font-medium text-[#1B2A4A]">{b.reference && <span className="mr-2 text-xs text-[#94A3B8]">#{b.reference}</span>}{b.title}{b.questionnaire?.company?.siret && <div data-testid={`admin-siret-${b.business_id}`} className="text-[11px] font-normal text-[#475569] mt-0.5">SIRET {b.questionnaire.company.siret}{b.questionnaire.company.legal_form && ` · ${b.questionnaire.company.legal_form}`}{b.questionnaire.company.address && ` · ${b.questionnaire.company.address}`}</div>}{b.owner_user_id && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#E0C173]">cédant</span>}</td>
                        <td className="p-4 text-[#475569]">{b.sector}</td>
                        <td className="p-4 text-[#475569]">{b.region}</td>
                        <td className="p-4">{b.profitability_rate != null ? `${b.profitability_rate}%` : "N.C."}</td>
                        <td className="p-4"><span className={`text-xs px-2 py-1 ${st.cls}`}>{st.label}</span></td>
                        <td className="p-4 text-right whitespace-nowrap">
                          {b.status === "pending" && <Button data-testid={`admin-approve-${b.business_id}`} size="sm" onClick={() => approve(b.business_id)} className="rounded-none bg-green-600 hover:bg-green-700 text-white mr-1"><CheckCircle2 className="w-4 h-4 mr-1" /> Publier</Button>}
                          {b.status === "sold" && !linkedIds.includes(b.business_id) && <Button data-testid={`admin-add-deal-${b.business_id}`} size="sm" variant="outline" onClick={() => setDealDraft(dealFromBusiness(b))} className="rounded-none mr-1"><Handshake className="w-4 h-4 mr-1" /> Ajouter aux cessions</Button>}
                          <Button data-testid={`admin-review-${b.business_id}`} size="sm" onClick={() => setReviewId(b.business_id)} className="rounded-none bg-[#1B2A4A] hover:bg-[#2a3d63] text-white"><Eye className="w-4 h-4 mr-1" /> {b.status === "pending" ? "Examiner" : "Aperçu"}</Button>
                          <Button data-testid={`admin-edit-${b.business_id}`} size="sm" variant="ghost" onClick={() => openEdit(b)} className="rounded-none"><Pencil className="w-4 h-4" /></Button>
                          <Button data-testid={`admin-delete-${b.business_id}`} size="sm" variant="ghost" onClick={() => remove(b.business_id)} className="rounded-none text-red-600"><Trash2 className="w-4 h-4" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <InquiriesPanel inquiries={inquiries} onChange={load} />
          </TabsContent>

          <TabsContent value="nda" className="mt-6">
            <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="admin-nda-list">
              {signatures.length === 0 ? (
                <p className="text-gray-400 py-10 text-center">Aucun accord de confidentialité signé pour le moment.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-[#475569]">
                    <tr><th className="p-4">Signataire</th><th className="p-4">Email</th><th className="p-4">Version</th><th className="p-4">Date de signature</th></tr>
                  </thead>
                  <tbody>
                    {signatures.map((s) => (
                      <tr key={s.signature_id} className="border-t border-gray-100">
                        <td className="p-4 font-medium text-[#1B2A4A] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E0C173]" /> {s.signed_name}</td>
                        <td className="p-4 text-[#475569]">{s.user_email}</td>
                        <td className="p-4 text-[#475569]">{s.version}</td>
                        <td className="p-4 text-[#475569]">{new Date(s.signed_at).toLocaleString("fr-FR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="rdv" className="mt-6">
            <DiscoveryRequestsPanel onChange={load} />
          </TabsContent>

          <TabsContent value="visits" className="mt-6"><VisitReportsPanel /></TabsContent>
          <TabsContent value="media" className="mt-6"><MediaRequestsPanel onChange={load} /></TabsContent>

          <TabsContent value="invitations" className="mt-6">
            <InvitationsPanel />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <ContactMessagesPanel />
          </TabsContent>

          <TabsContent value="deals" className="mt-6"><SoldDealsPanel version={dealsVersion} onEdit={setDealDraft} /></TabsContent>
          <TabsContent value="alerts" className="mt-6"><AdminAlertsPanel /></TabsContent>
          <TabsContent value="blog" className="mt-6"><ArticlesPanel /></TabsContent>
          <TabsContent value="emails" className="mt-6"><EmailCenter /></TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-6">
            <EmailSettings />
            <SocialSettings />
          </TabsContent>
        </Tabs>
      </div>

      <BusinessFormDialog open={open} onOpenChange={setOpen} editing={editing} initial={initial} onSubmit={save} showStatus />
      <SoldDealDialog deal={dealDraft} onOpenChange={(v) => !v && setDealDraft(null)} onSaved={dealSaved} />
      <BusinessReviewDialog businessId={reviewId} onOpenChange={(v) => !v && setReviewId(null)} onDone={load} />
    </div>
  );
}
